import { useState, useEffect } from "react";
import Select from "react-select";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Filter, RotateCcw } from "lucide-react";
import Campo from "../posgrados/Campo";
import Modalidad from "../posgrados/Modalidad";

const BASE_URL = "https://be-ofertas.utpl.edu.ec";

const facultadSlugMap = {
  empresas: "Ciencias Económicas y Empresariales",
  exactas: "Ciencias Exactas y Naturales",
  salud: "Ciencias de la Salud",
  sociales: "Ciencias Sociales, Educación y Humanidades",
  juridicas: "Ciencias Jurídicas y Políticas",
  ingenieria: "Ingenierías y Arquitectura",
  negocios: "UTPL Escuela de Negocios",
};

const facultadNameToSlug = Object.fromEntries(
  Object.entries(facultadSlugMap).map(([slug, name]) => [name, slug]),
);

const programaMap = {
  maestria: "Maestría",
  especializacion: "Especialización",
  doctorado: "Doctorado",
};

const modalidadMap = {
  en_linea: "En línea",
  presencial: "Presencial",
  hibrida: "Híbrida",
};

const facultadMap = {
  facultad_de_ciencias_economicas_y_empresariales:
    "Ciencias Económicas y Empresariales",
  facultad_de_ciencias_exactas_y_naturales: "Ciencias Exactas y Naturales",
  facultad_de_ciencias_de_la_salud: "Ciencias de la Salud",
  facultad_de_ciencias_sociales_educacion_y_humanidades:
    "Ciencias Sociales, Educación y Humanidades",
  facultad_de_ciencias_juridicias_y_politicas:
    "Ciencias Jurídicas y Políticas",
  facultad_de_ingenierias_y_arquitectura: "Ingenierías y Arquitectura",
  escuela_de_negocios: "UTPL Escuela de Negocios",
};

const optionsPrograma = [
  { value: "todos", label: "Todos los programas" },
  { value: "Maestría", label: "Maestría" },
  { value: "Especialización", label: "Especialización" },
  { value: "Doctorado", label: "Doctorado" },
];

const optionsFacultad = [
  { value: "todos", label: "Toda la oferta" },
  {
    value: "Ciencias Económicas y Empresariales",
    label: "Ciencias Económicas y Empresariales",
  },
  {
    value: "Ciencias Exactas y Naturales",
    label: "Ciencias Exactas y Naturales",
  },
  { value: "Ciencias de la Salud", label: "Ciencias de la Salud" },
  {
    value: "Ciencias Sociales, Educación y Humanidades",
    label: "Ciencias Sociales, Educación y Humanidades",
  },
  {
    value: "Ciencias Jurídicas y Políticas",
    label: "Ciencias Jurídicas y Políticas",
  },
  {
    value: "Ingenierías y Arquitectura",
    label: "Ingenierías y Arquitectura",
  },
  { value: "UTPL Escuela de Negocios", label: "UTPL Escuela de Negocios" },
];

const optionsModalidad = [
  { value: "todos", label: "Todas las modalidades" },
  { value: "En línea", label: "En línea" },
  { value: "Presencial", label: "Presencial" },
  { value: "Híbrida", label: "Híbrida" },
];

const badgeColors = {
  Maestría: "bg-blue-100 text-blue-800 border border-blue-200",
  Especialización: "bg-purple-100 text-purple-800 border border-purple-200",
  Doctorado: "bg-emerald-100 text-emerald-800 border border-emerald-200",
};

const getImageUrl = (node, included, fieldName) => {
  const mediaId = node.relationships?.[fieldName]?.data?.id;
  if (!mediaId) return null;

  const media = included.find(
    (item) => item.type === "media--image" && item.id === mediaId,
  );
  const fileId = media?.relationships?.field_media_image?.data?.id;
  if (!fileId) return null;

  const file = included.find(
    (item) => item.type === "file--file" && item.id === fileId,
  );

  return file?.attributes?.uri?.url || null;
};

const cleanText = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

function OfferPosgrados() {
  // --- Estados de Datos de la API ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Funciones para parámetros iniciales de URL ---
  const getInitialValue = (param, options) => {
    const params = new URLSearchParams(window.location.search);
    let value = params.get(param);

    if (param === "facultad" && facultadSlugMap[value]) {
      value = facultadSlugMap[value];
    }

    return options.find((o) => o.value === value) || options[0];
  };

  // --- Estados de Filtros ---
  const [programaSeleccionado, setProgramaSeleccionado] = useState(
    getInitialValue("programa", optionsPrograma),
  );
  const [facultadSeleccionada, setFacultadSeleccionada] = useState(
    getInitialValue("facultad", optionsFacultad),
  );
  const [modalidadSeleccionada, setModalidadSeleccionada] = useState(
    getInitialValue("modalidad", optionsModalidad),
  );

  // --- Fetch inicial de datos ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const endpoint =
          `${BASE_URL}/jsonapi/node/posgrado?` +
          "fields[node--posgrado]=title,field_tipo_de_programa,field_facultad,field_modalidad,field_duracion,field_inicio_clases,field_campo_amplio,field_url_al_contenido,field_imagen_portada" +
          "&fields[media--image]=field_media_image" +
          "&fields[file--file]=uri" +
          "&include=field_imagen_portada,field_imagen_portada.field_media_image";

        const urls = [endpoint, `${endpoint}&page[offset]=50&page[limit]=50`];

        const responses = await Promise.all(urls.map((url) => fetch(url)));
        const jsons = await Promise.all(responses.map((r) => r.json()));

        const allItems = jsons.flatMap((json) => json.data || []);
        const allIncluded = jsons.flatMap((json) => json.included || []);

        const uniqueItems = [
          ...new Map(allItems.map((item) => [item.id, item])).values(),
        ];

        const transformed = uniqueItems.map((item) => {
          const attrs = item.attributes;
          const imagePath = getImageUrl(
            item,
            allIncluded,
            "field_imagen_portada",
          );

          return {
            id: item.id,
            title: attrs.title,
            field_programa:
              programaMap[attrs.field_tipo_de_programa] ||
              attrs.field_tipo_de_programa,
            field_facultad:
              facultadMap[attrs.field_facultad] || attrs.field_facultad,
            field_modalidad:
              modalidadMap[attrs.field_modalidad] || attrs.field_modalidad,
            field_duracion: attrs.field_duracion || "",
            field_inicio: attrs.field_inicio_clases || "",
            field_inicio_clases: attrs.field_inicio_clases || "",
            field_campo_amplio: attrs.field_campo_amplio || "",
            field_url: attrs.field_url_al_contenido || "",
            field_imagen_portada: imagePath
              ? BASE_URL + imagePath
              : "https://via.placeholder.com/600x400",
          };
        });

        setData(transformed);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // --- Sincronización de Filtros con la URL ---
  useEffect(() => {
    const params = new URLSearchParams();

    if (programaSeleccionado.value !== "todos")
      params.set("programa", programaSeleccionado.value);

    if (facultadSeleccionada.value !== "todos") {
      const slug = facultadNameToSlug[facultadSeleccionada.value];
      params.set("facultad", slug);
    }

    if (modalidadSeleccionada.value !== "todos")
      params.set("modalidad", modalidadSeleccionada.value);

    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");

    window.history.replaceState({}, "", newUrl);
  }, [programaSeleccionado, facultadSeleccionada, modalidadSeleccionada]);

  const limpiarFiltros = () => {
    setProgramaSeleccionado(optionsPrograma[0]);
    setFacultadSeleccionada(optionsFacultad[0]);
    setModalidadSeleccionada(optionsModalidad[0]);
  };

  // --- Filtrado y ordenamiento de la oferta ---
  const filtered = data.filter((item) => {
    if (
      programaSeleccionado.value !== "todos" &&
      item.field_programa !== programaSeleccionado.value
    ) {
      return false;
    }
    if (
      facultadSeleccionada.value !== "todos" &&
      item.field_facultad !== facultadSeleccionada.value
    ) {
      return false;
    }

    if (modalidadSeleccionada.value !== "todos") {
      if (!item.field_modalidad) return false;

      const seleccionLimpia = cleanText(modalidadSeleccionada.value);
      let modalidadesDelItem = [];

      if (Array.isArray(item.field_modalidad)) {
        modalidadesDelItem = item.field_modalidad.map((m) =>
          cleanText(m).replace("_", " "),
        );
      } else if (typeof item.field_modalidad === "string") {
        modalidadesDelItem = [cleanText(item.field_modalidad).replace("_", " ")];
      }

      const coincideDirecto = modalidadesDelItem.includes(seleccionLimpia);
      const coincideTextoCruzado = modalidadesDelItem.some(
        (m) => m.includes(seleccionLimpia) || seleccionLimpia.includes(m),
      );

      if (!coincideDirecto && !coincideTextoCruzado) {
        return false;
      }
    }

    return true;
  });

  const filteredData = [...filtered].sort((a, b) => {
    const dateA = a.field_inicio_clases || "";
    const dateB = b.field_inicio_clases || "";

    const dateComparison = dateB.localeCompare(dateA, "es");
    if (dateComparison !== 0) {
      return dateComparison;
    }

    const titleA = cleanText(a.title);
    const titleB = cleanText(b.title);
    return titleA.localeCompare(titleB, "es");
  });

  if (loading) return <p className="px-16 md:px-10 py-6">Cargando posgrados...</p>;
  if (error) return <p className="px-16 md:px-10 py-6 text-red-500">Error: {error}</p>;

  return (
    <div className="px-16 md:px-10">
      <div className="wrapper-filtros layout9">
        <div>
          <p>
            <b>Programa</b>
          </p>
          <Select
            options={optionsPrograma}
            value={programaSeleccionado}
            onChange={setProgramaSeleccionado}
          />
        </div>

        <div>
          <p>
            <b>Facultad</b>
          </p>
          <Select
            options={optionsFacultad}
            value={facultadSeleccionada}
            onChange={setFacultadSeleccionada}
          />
        </div>

        <div>
          <p>
            <b>Modalidad</b>
          </p>
          <Select
            options={optionsModalidad}
            value={modalidadSeleccionada}
            onChange={setModalidadSeleccionada}
          />
        </div>

        <div>
          <button
            className="btn-reset-filtros flex items-center gap-2"
            onClick={limpiarFiltros}
          >
            <RotateCcw size={16} />
            Limpiar filtros
          </button>
        </div>
      </div>

      <div className="mb-6 flex items-center justify-between">
        <p className="text-gray-700 flex items-center gap-2">
          <Filter size={16} />
          <span className="font-bold">{filteredData.length}</span> programas
          encontrados
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {filteredData.map((item) => (
          <motion.a
            key={item.id}
            href={item.field_url}
            rel="noreferrer"
            className="group relative block bg-white rounded-2xl shadow-md overflow-hidden"
            initial="rest"
            animate="rest"
            whileHover="hover"
          >
            <div className="h-40 overflow-hidden">
              <img
                src={item.field_imagen_portada}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
            </div>

            <motion.div
              variants={{
                rest: { scale: 1, opacity: 0.8 },
                hover: { scale: 1.2, opacity: 1 },
              }}
              className="absolute top-4 right-4 bg-white/90 backdrop-blur-md rounded-full p-2 shadow-md"
            >
              <Plus size={12} />
            </motion.div>

            <div className="p-5 space-y-3">
              <div className="flex gap-2 flex-wrap">
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-md ${
                    badgeColors[item.field_programa] || "bg-gray-100 text-gray-800"
                  }`}
                >
                  {item.field_programa}
                </span>
              </div>

              <h3 className="text-base font-bold leading-tight p-0">
                {item.title}
              </h3>

              {item.field_modalidad && (
                <div
                  className="
                    [&_p]:text-sm 
                    [&_p]:text-gray-600 
                    [&_p]:p-0
                    [&_p]:inline-flex
                    [&_p]:leading-none
                    [&_ul]:p-0
                    [&_ul]:m-0
                    [&_li]:text-sm 
                    [&_li]:text-gray-600 
                    [&_li]:py-0      
                    [&_li]:pt-1           
                    [&_li]:leading-none"
                >
                  <p>
                    <strong>Modalidad: </strong>
                  </p>
                  <Modalidad value={item.field_modalidad} />
                </div>
              )}

              {item.field_duracion && (
                <p className="text-sm text-gray-600 p-0 leading-none">
                  <strong>Duración:</strong>{" "}
                  {item.field_duracion
                    .replaceAll("_", " ")
                    .replace("anos", "años")
                    .replace("ano", "año")}
                </p>
              )}

              {item.field_facultad && (
                <p className="text-sm text-gray-600 p-0 leading-none">
                  <strong>Facultad:</strong> {item.field_facultad}
                </p>
              )}

              {item.field_campo_amplio && (
                <p className="text-sm text-gray-600 p-0 leading-none">
                  <strong>Campo: </strong>
                  <Campo value={item.field_campo_amplio} />
                </p>
              )}

              {item.field_inicio_clases && (
                <p className="text-sm text-gray-600 p-0 leading-none">
                  <strong>Inicio de clases:</strong>{" "}
                  {item.field_inicio_clases}
                </p>
              )}
            </div>

            <motion.div
              variants={{
                rest: { opacity: 0 },
                hover: { opacity: 1 },
              }}
              className="absolute inset-0 bg-black/10 flex items-center justify-center"
            >
              <ArrowRight size={44} className="text-white" />
            </motion.div>
          </motion.a>
        ))}
      </div>
    </div>
  );
}

export default OfferPosgrados;